import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SaleBanner from "@/components/SaleBanner";
import { 
  LogIn, 
  Eye, 
  CreditCard, 
  FileText, 
  ChevronRight,
  Search,
  History,
  Download,
  Shield,
  X,
  Users,
  Building2,
  Newspaper,
  Database,
  Lock,
  Zap,
  FileBarChart,
  Mail,
  Phone,
  Scale,
  ShieldCheck,
  Store,
  Globe,
  Rocket,
  TrendingUp,
  Sparkles
} from "lucide-react";

const CONTACT_EMAIL = "infoosintquebec@gmail.com";

const LandingPage = () => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [showAcquisitionDetail, setShowAcquisitionDetail] = useState(false);

  const handleShopClosed = () => {
    toast.error("Boutique fermée — La plateforme est à vendre", {
      description: `Pour toute demande d'acquisition : ${CONTACT_EMAIL}`,
      duration: 5000,
    });
  };

  // Professionnels cibles
  const targetAudience = [
    {
      icon: <Search className="w-8 h-8" />,
      title: "Enquêteurs",
      description: "Privés et corporatifs"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Analystes",
      description: "Sécurité et renseignement"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Forces de l'ordre",
      description: "Organismes gouvernementaux"
    },
    {
      icon: <Newspaper className="w-8 h-8" />,
      title: "Journalistes",
      description: "Investigation"
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Chercheurs",
      description: "Analystes de données"
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Avocats",
      description: "Cabinets juridiques"
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: "Assurances",
      description: "Compagnies d'assurance"
    }
  ];

  // Fonctionnalités principales
  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Recherche multi-sources",
      description: "Interrogez plusieurs plateformes simultanément en temps réel"
    },
    {
      icon: <FileBarChart className="w-6 h-6" />,
      title: "Rapports personnalisables",
      description: "Exportez vos résultats en PDF ou Excel selon vos besoins"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Interface sécurisée",
      description: "Plateforme intuitive avec gestion des accès et historique complet"
    },
    {
      icon: <History className="w-6 h-6" />,
      title: "Historique complet",
      description: "Gardez une trace de toutes vos recherches et investigations"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Sale banner - fixed top */}
      <div className="fixed top-0 left-0 right-0 z-[60]">
        <SaleBanner />
      </div>
      {/* Header */}
      <header className="fixed top-[44px] sm:top-[40px] left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-sne.png" alt="SNE Logo" className="h-10 w-auto" />
          </div>
          <nav className="flex items-center gap-2 md:gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-400 hover:text-white hidden sm:flex"
              onClick={() => setShowPreview(true)}
            >
              <Eye className="w-4 h-4 mr-2" />
              Aperçu
            </Button>
            <Button 
              variant="ghost" 
              className="text-slate-400 hover:text-white hidden sm:flex"
              onClick={() => navigate("/legal")}
            >
              <FileText className="w-4 h-4 mr-2" />
              Mentions légales
            </Button>
            <Button 
              onClick={() => navigate("/login")}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Connexion
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 sm:pt-36 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6">
            <img 
              src="/logo-sne.png" 
              alt="SNE - Solutions Numérique en Enquêtes" 
              className="h-32 md:h-40 w-auto mx-auto mb-4"
            />
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Bienvenue sur la plateforme{" "}
            <span className="text-amber-500">OSINT</span>
          </h1>
          <h2 className="text-xl md:text-2xl text-amber-400 font-semibold mb-6">
            Solutions Numériques d&apos;Enquêtes
          </h2>
          
          <p className="text-base md:text-lg text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Accédez à des outils de recherche et d&apos;analyse OSINT (Open Source Intelligence) 
            de niveau professionnel. Notre plateforme vous permet de{" "}
            <span className="text-white font-medium">collecter</span>,{" "}
            <span className="text-white font-medium">analyser</span> et{" "}
            <span className="text-white font-medium">exploiter</span>{" "}
            des données publiques pour vos enquêtes et investigations.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white text-base px-6 py-5"
              onClick={handleShopClosed}
              data-testid="view-subscriptions-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Voir les abonnements
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <Button 
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-base px-6 py-5 shadow-lg shadow-amber-500/30 acquisition-detail-pulse"
              onClick={() => setShowAcquisitionDetail(true)}
              data-testid="acquisition-detail-btn-hero"
            >
              <FileBarChart className="w-5 h-5 mr-2" />
              Dossier acquisition · Acquisition brief
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-amber-600/50 text-amber-400 hover:bg-amber-600/10 text-base px-6 py-5"
              onClick={() => setShowPreview(true)}
            >
              <Eye className="w-5 h-5 mr-2" />
              Voir l&apos;aperçu
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800 text-base px-6 py-5"
              onClick={() => navigate("/login")}
            >
              <LogIn className="w-5 h-5 mr-2" />
              Connexion
            </Button>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-16 px-6 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Conçu pour les professionnels
            </h2>
            <p className="text-slate-400">
              Une solution adaptée à vos besoins d&apos;investigation
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {targetAudience.map((audience, index) => (
              <div 
                key={index}
                className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-amber-600/40 transition-all text-center group"
              >
                <div className="text-amber-500 mb-3 flex justify-center group-hover:scale-110 transition-transform">
                  {audience.icon}
                </div>
                <h3 className="text-sm font-semibold text-white mb-1">{audience.title}</h3>
                <p className="text-slate-400 text-xs">{audience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Fonctionnalités principales
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl bg-gradient-to-br from-slate-800/60 to-slate-800/30 border border-slate-700/50 hover:border-amber-600/40 transition-all flex gap-4"
              >
                <div className="text-amber-500 flex-shrink-0 mt-1">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-amber-900/20 via-slate-900 to-slate-900 border-y border-amber-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Commencez dès maintenant
          </h2>
          <p className="text-slate-300 mb-8 text-base md:text-lg">
            Essayez notre <span className="text-amber-400 font-semibold">plan démo gratuit de 3 jours</span> ou 
            choisissez un abonnement adapté à vos besoins.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white text-base px-8 py-6"
              onClick={() => window.open('https://solutions-numeriques-enquetes.com/', '_blank')}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Plan Démo 3 jours gratuit
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-amber-600/50 text-amber-400 hover:bg-amber-600/10 text-base px-8 py-6"
              onClick={() => window.open('https://solutions-numeriques-enquetes.com/', '_blank')}
            >
              <Users className="w-5 h-5 mr-2" />
              Abonnements (1-10 licences)
            </Button>
          </div>
          
          {/* Contact for enterprise */}
          <div className="p-5 rounded-xl bg-slate-800/50 border border-slate-700/50 inline-block">
            <p className="text-slate-300 mb-3">
              <span className="text-white font-medium">Plus de 10 licences</span> ou des besoins personnalisés ?
            </p>
            <a 
              href="mailto:infoosintquebec@gmail.com?subject=Demande%20de%20licences%20entreprise%20SNE"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors font-medium"
            >
              <Mail className="w-5 h-5" />
              Contactez notre équipe
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Acquisition Opportunity Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-amber-950/40 via-slate-900 to-orange-950/40 border-y border-amber-800/30" data-testid="acquisition-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs md:text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              OPPORTUNITÉ D'ACQUISITION · ACQUISITION OPPORTUNITY
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Plateforme <span className="text-amber-400">à vendre</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed mb-2">
              Solution OSINT clé-en-main, revenue-ready, prête pour distribution internationale.
            </p>
            <p className="text-sm text-slate-400 max-w-3xl mx-auto italic mb-6">
              Turnkey OSINT platform, revenue-ready, prepared for international rollout.
            </p>
            {/* Prominent "Détail" button at the top */}
            <button
              onClick={() => setShowAcquisitionDetail(true)}
              data-testid="acquisition-detail-btn-top"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-3 md:py-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-sm sm:text-base md:text-lg transition-all shadow-lg shadow-amber-500/30 hover:shadow-amber-400/60 hover:scale-105 acquisition-detail-pulse"
            >
              <FileBarChart className="w-5 h-5 flex-shrink-0" />
              <span className="hidden sm:inline">Voir le dossier complet · View full brief</span>
              <span className="sm:hidden">Voir le dossier · Full brief</span>
              <ChevronRight className="w-5 h-5 flex-shrink-0" />
            </button>
          </div>

          {/* Key numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="text-center p-5 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">36+</div>
              <div className="text-xs md:text-sm text-slate-400">Sources OSINT<br/>intégrées</div>
            </div>
            <div className="text-center p-5 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">MVP</div>
              <div className="text-xs md:text-sm text-slate-400">Fonctionnel<br/>en production</div>
            </div>
            <div className="text-center p-5 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">SaaS</div>
              <div className="text-xs md:text-sm text-slate-400">Multi-licences<br/>par commande</div>
            </div>
            <div className="text-center p-5 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-1">FR/EN</div>
              <div className="text-xs md:text-sm text-slate-400">Architecture<br/>bilingue-ready</div>
            </div>
          </div>

          {/* What's included */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="p-6 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-3 mb-4">
                <Rocket className="w-6 h-6 text-amber-400" />
                <h3 className="text-xl font-bold text-white">Prêt à l'exploitation</h3>
              </div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Intégration Shopify (webhooks + génération de codes)</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Système de licences multi-seats (1 commande → N utilisateurs)</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Panneau d'administration complet (users, codes, exports)</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Anti-fraude intégré (IP / fingerprint / trial tracking)</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Exports Excel &amp; PDF natifs</li>
              </ul>
            </div>
            <div className="p-6 rounded-xl bg-slate-900/60 border border-slate-800">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-amber-400" />
                <h3 className="text-xl font-bold text-white">Potentiel de croissance</h3>
              </div>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Marchés : forces de l'ordre, avocats, assurances, journalistes</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Extensible aux marchés US, EU, LATAM (ajout de sources)</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Conformité <strong>Loi 25 / LPRPDE</strong> — pages légales prêtes</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Stack moderne : FastAPI + React + MongoDB (facile à faire évoluer)</li>
                <li className="flex gap-2"><span className="text-amber-400">✓</span> Code source complet + documentation technique</li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="inline-block p-6 md:p-8 rounded-2xl bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/40 backdrop-blur-sm max-w-2xl w-full">
              <p className="text-slate-200 mb-2 text-sm md:text-base">
                <Globe className="w-4 h-4 inline mr-1 text-amber-400" />
                <strong>Acheteurs sérieux uniquement</strong> — Serious buyers only
              </p>
              <p className="text-slate-400 text-xs md:text-sm mb-5 italic">
                Pour un développement et une distribution internationale · For international development &amp; distribution
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Acquisition%20de%20la%20plateforme%20SNE%20-%20Serious%20acquisition%20inquiry&body=Bonjour%2C%0A%0AJe%20suis%20int%C3%A9ress%C3%A9(e)%20par%20l%27acquisition%20de%20la%20plateforme%20SNE.%0AMerci%20de%20me%20contacter%20avec%20les%20d%C3%A9tails%20suivants%20%3A%0A-%20Prix%20demand%C3%A9%0A-%20Documentation%20technique%0A-%20Statistiques%20d%27utilisation%0A%0AHello%2C%0A%0AI%20am%20interested%20in%20acquiring%20the%20SNE%20platform.%0APlease%20contact%20me%20with%20the%20following%20details%3A%0A-%20Asking%20price%0A-%20Technical%20documentation%0A-%20Usage%20statistics%0A%0A`}
                  data-testid="acquisition-contact-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-3 md:py-4 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm sm:text-base md:text-lg transition-all shadow-lg hover:shadow-amber-500/50 hover:scale-105"
                >
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <span>Nous contacter · Contact us</span>
                  <ChevronRight className="w-5 h-5 flex-shrink-0" />
                </a>
                <button
                  onClick={() => setShowAcquisitionDetail(true)}
                  data-testid="acquisition-detail-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-3 md:py-4 rounded-xl bg-transparent border-2 border-amber-500 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 font-bold text-sm sm:text-base md:text-lg transition-all"
                >
                  <FileBarChart className="w-5 h-5 flex-shrink-0" />
                  <span>Détail · Details</span>
                </button>
              </div>
              <p className="text-slate-500 text-xs mt-4">
                {CONTACT_EMAIL}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo-sne.png" alt="SNE Logo" className="h-8 w-auto" />
              <span className="text-slate-500 text-sm">© 2025 SNE - Solutions Numérique en Enquêtes</span>
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate("/legal")}
                className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
              >
                <FileText className="w-4 h-4" />
                Conditions et Politiques
              </button>
              <a 
                href="mailto:infoosintquebec@gmail.com"
                className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
              >
                <Mail className="w-4 h-4" />
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Preview Modal */}
      {showPreview && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 overflow-y-auto"
          onClick={() => setShowPreview(false)}
        >
          <div className="min-h-screen py-8 px-4">
            <div 
              className="max-w-5xl mx-auto bg-slate-900 rounded-2xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Aperçu de la plateforme</h2>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowPreview(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Image 1 - Recherche avec catégories */}
              <div className="mb-6 rounded-lg overflow-hidden border border-slate-700">
                <div className="bg-slate-800 p-3">
                  <h3 className="font-semibold text-white">Page de Recherche</h3>
                  <p className="text-slate-400 text-sm">Interface de recherche multi-sites avec catégories personnalisables</p>
                </div>
                <div className="bg-slate-950 p-2">
                  <img 
                    src="/preview-search.png" 
                    alt="Page de Recherche"
                    className="w-full h-auto rounded"
                  />
                </div>
              </div>

              {/* Image 2 - Sites de recherche */}
              <div className="mb-6 rounded-lg overflow-hidden border border-slate-700">
                <div className="bg-slate-800 p-3">
                  <h3 className="font-semibold text-white">Sites de recherche disponibles</h3>
                  <p className="text-slate-400 text-sm">Plateformes OSINT organisées par catégories</p>
                </div>
                <div className="bg-slate-950 p-2">
                  <img 
                    src="/preview-sites.jpg" 
                    alt="Sites de recherche disponibles"
                    className="w-full h-auto rounded object-contain max-h-[600px]"
                  />
                </div>
              </div>

              <div className="text-center">
                <Button 
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleShopClosed}
                  data-testid="subscribe-preview-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  S&apos;abonner pour accéder
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Acquisition Detail Modal */}
      {showAcquisitionDetail && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 overflow-y-auto backdrop-blur-sm"
          onClick={() => setShowAcquisitionDetail(false)}
          data-testid="acquisition-detail-modal"
        >
          <div className="min-h-screen py-8 px-4">
            <div
              className="max-w-5xl mx-auto bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 rounded-2xl border border-amber-500/40 shadow-2xl shadow-amber-500/20 p-6 md:p-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-8 gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-semibold mb-3">
                    <Sparkles className="w-3.5 h-3.5" />
                    DOSSIER D'ACQUISITION · ACQUISITION BRIEF
                  </div>
                  <h2 className="text-2xl md:text-4xl font-bold text-white">
                    Détail de l'opportunité
                  </h2>
                  <p className="text-slate-400 text-sm md:text-base mt-2 italic">
                    Full acquisition opportunity details
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowAcquisitionDetail(false)}
                  className="text-slate-400 hover:text-white flex-shrink-0"
                  data-testid="close-acquisition-detail-btn"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>

              {/* Section 1: Chiffres percutants */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Chiffres percutants</h3>
                </div>
                <div className="space-y-3 pl-2">
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">36+ sources OSINT intégrées</div>
                        <div className="text-sm text-slate-400">Google, LinkedIn, Facebook, Twitter, Corporations Canada, et bien plus</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Multi-tenant SaaS-ready</div>
                        <div className="text-sm text-slate-400">Multi-licences par commande Shopify (1 → N utilisateurs)</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Marchés cibles</div>
                        <div className="text-sm text-slate-400">Forces de l'ordre · Avocats · Assurances · Journalistes · Enquêteurs privés</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Points de vente clés */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Points de vente clés</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-3 pl-2">
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <CreditCard className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Revenue-ready</div>
                        <div className="text-sm text-slate-400">Intégration Shopify prête + système de licences complet</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <FileBarChart className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Admin panel complet</div>
                        <div className="text-sm text-slate-400">Gestion utilisateurs, codes, alertes anti-abus, exports Excel/PDF</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Anti-fraud built-in</div>
                        <div className="text-sm text-slate-400">Tracking IP / fingerprint pour éviter les abus démo</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <Scale className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Conformité Loi 25 / LPRPDE</div>
                        <div className="text-sm text-slate-400">Pages légales prêtes, NDA admin, politique de vie privée</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Angle international */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white">Angle international</h3>
                </div>
                <div className="space-y-3 pl-2">
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-blue-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Extensible easily to US/EU markets</div>
                        <div className="text-sm text-slate-400">Ajout de sources spécifiques par marché (SEC, Companies House, LEI, etc.)</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/60 hover:border-blue-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-white mb-1">Bilingual-ready architecture</div>
                        <div className="text-sm text-slate-400">FR/EN — infrastructure prête pour l'ajout d'autres langues</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Footer */}
              <div className="mt-8 pt-6 border-t border-slate-800 text-center">
                <p className="text-slate-300 mb-4 text-sm md:text-base">
                  Prêt à discuter les termes de l'acquisition ?
                  <span className="block text-slate-500 italic text-xs md:text-sm mt-1">Ready to discuss acquisition terms?</span>
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Acquisition%20de%20la%20plateforme%20SNE%20-%20Serious%20acquisition%20inquiry`}
                  className="inline-flex items-center gap-2 px-6 md:px-8 py-3 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold transition-all shadow-lg hover:shadow-amber-500/50"
                  data-testid="acquisition-modal-contact-btn"
                >
                  <Mail className="w-5 h-5" />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes acquisitionPulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 0 14px rgba(245, 158, 11, 0);
            transform: scale(1.03);
          }
        }
        .acquisition-detail-pulse {
          animation: acquisitionPulse 2.4s ease-in-out infinite;
        }
        .acquisition-detail-pulse:hover {
          animation: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .acquisition-detail-pulse { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
